import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Handshake } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * How somebody learns they have been asked into the circle.
 *
 * There is no link and nothing is sent anywhere — see
 * utils/sworn_invites.py for the argument. The invitation waits inside the
 * signed-in application, so this component is the entire delivery mechanism.
 *
 * It renders NOTHING for everybody else, and that is the whole of its
 * discipline: the request 404s for anybody without an offer, exactly as every
 * other Sworn route does, so nothing about the circle reaches a screen that is
 * not supposed to know it exists (§3.1).
 */
const InvitationNotice = ({ variant = 'strip' }) => {
  const navigate = useNavigate();
  const [invited, setInvited] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_URL}/api/sworn/invitation`, { withCredentials: true })
      .then(() => { if (!cancelled) setInvited(true); })
      .catch(() => { /* no offer waiting, and nothing to say about it */ });
    return () => { cancelled = true; };
  }, []);

  if (!invited) return null;

  return (
    <button
      type="button"
      onClick={() => navigate('/sworn/invitation')}
      // 'card' sits among Room's doors; 'strip' runs under a page header.
      className={variant === 'card'
        ? 'w-full flex items-center gap-3 rounded-xl border px-4 py-3 bg-card/85 backdrop-blur-sm hover:bg-card transition-colors text-left'
        : 'w-full flex items-center gap-3 px-4 py-3 border-b border-border bg-card/60 hover:bg-accent transition-colors text-left'}
      style={variant === 'card' ? { borderColor: 'hsl(var(--gold) / 0.45)' } : undefined}
      data-testid="sworn-invitation-notice"
    >
      <Handshake
        className="w-5 h-5 shrink-0"
        style={{ color: 'hsl(var(--gold))' }}
        weight="fill"
      />
      <div className="min-w-0">
        <p className="text-sm font-medium leading-tight">You have been asked to join the Sworn</p>
        {/* No countdown. An offer with a clock on it is an offer with pressure
            on it, and §7.1 is explicit that refusing must not be expensive.
            The date is on the page itself for anybody who wants it. */}
        <p className="text-xs text-muted-foreground">Read it when you like.</p>
      </div>
    </button>
  );
};

export default InvitationNotice;
